<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import FooterWrapper from "@/components/FooterWrapper";

export default {
  // Included components
  components: {
    FooterWrapper,
  },

  data() {
    return {
      // The user's email
      email: "",

      // The user's password
      password: "",

      // Whether the two factor code is being requested
      twoFactor: false,

      // The two factor input code
      twoFactorCode: "",

      // The next path to go to after signing in
      next: null,
    };
  },

  computed: {
    // Mapped vuex state
    ...mapState(["teams", "activeTeamId", "teamContracts"]),
    ...mapGetters(["authenticated", "can"]),
  },

  // When the component is created, get the next
  // field from the parameters
  created() {
    this.next = this.$route.query.next;
  },

  methods: {
    // Attempt to sign in with the given credentials
    async submit() {
      // Sign in given the form credentials
      let result = await this.signIn({
        email: this.email,
        password: this.password,
        twoFactorCode: this.twoFactor ? this.twoFactorCode : undefined,
      });

      // If the authentication failed because two factor is required,
      // show the two-factor form. If it failed for another reason,
      // show the "sign in unsuccessful" toast
      if (result.error !== null) {
        switch (result.error.code) {
          case "INVALID_CREDENTIALS":
            this.pushToast({
              title: "Error",
              message: "Invalid username or password",
              variant: "danger",
            });
            break;
          case "EMAIL_NOT_CONFIRMED":
            this.pushToast({
              title: "Error",
              message:
                "This email has not been confirmed. Please look for a confirmation email to log in.",
              variant: "danger",
            });
            break;
          case "TWO_FACTOR_REQUIRED":
            this.twoFactor = true;
            break;
          default:
            this.pushToast({
              title: "Error",
              message: "Sign In Failed",
              variant: "danger",
            });
            break;
        }
      }

      // Edge case: unsuccessful authentication
      if (!this.authenticated) {
        return;
      }

      // Load user information
      await Promise.all([
        this.getUser(),
        this.getTeams(),
        this.getPermissions(),
        this.getAccountRoles(),
      ]);
      // Get the first team to set active
      const firstTeam = Object.keys(this.teams)[0];
      // Set first team to active
      this.setActiveTeamId(firstTeam);
      // Retrieve the Contracts for the Active Team ID
      // This API Call returns all contracts for the team in reverse order of creation
      // Check the most recent contract for any incomplete fields and reroute to proper page to complete
      await this.getTeamContracts();
      let most_recent_contract = this.teamContracts[0];
      if (this.can.billing.manage) {
        if (most_recent_contract) {
          if (!most_recent_contract.payment_method_ingested) {
            await this.$router.push("/ingest-payment-method");
          } else if (!most_recent_contract.onboarding_invoice_created) {
            await this.$router.push("/confirm-charge");
          }
        }
        await this.$router.push({ path: "/" });
      }
      await this.$router.push({ path: "/" });
    },

    // Mapped vuex actions
    ...mapActions([
      "signIn",
      "getUser",
      "getTeams",
      "getAccountRoles",
      "getPermissions",
      "getTeamContracts",
    ]),
    ...mapMutations(["setActiveTeamId", "pushToast"]),
  },
};
</script>

<template>
  <FooterWrapper>
    <b-container class="mt-3">
      <b-row align-h="center">
        <b-col lg="9" xl="8">
          <div v-if="twoFactor">
            <h1>Two-factor Required</h1>
            <b-card>
              <b-card-text>
                <b-form @submit.stop.prevent="submit">
                  <b-form-group
                    id="two-factor-code-group"
                    label="Two-factor Code"
                    label-for="two-factor-code"
                  >
                    <b-form-input
                      required
                      id="two-factor-code"
                      v-model="twoFactorCode"
                      placeholder="Two-factor Code"
                    ></b-form-input>
                  </b-form-group>
                  <b-button type="submit" variant="primary">Log In</b-button>
                </b-form>
              </b-card-text>
            </b-card>
          </div>
          <div v-else>
            <h1>Log In</h1>
            <b-card>
              <b-card-text>
                <b-form @submit.stop.prevent="submit">
                  <b-form-group
                    id="email-group"
                    label="Email"
                    label-for="email"
                  >
                    <b-form-input
                      required
                      id="email"
                      type="email"
                      v-model="email"
                      placeholder="Email"
                      oninput="let p=this.selectionStart;this.value=this.value.toLowerCase();this.setSelectionRange(p, p);"
                    ></b-form-input>
                  </b-form-group>

                  <b-form-group
                    id="password-group"
                    label="Password"
                    label-for="password"
                  >
                    <b-form-input
                      required
                      id="password"
                      type="password"
                      v-model="password"
                      placeholder="Password"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      <b-link
                        :to="{ path: '/password-reset', query: { email } }"
                      >
                        Forgot your password?
                      </b-link>
                    </small>
                  </b-form-group>

                  <b-button type="submit" variant="primary">Log In</b-button>
                </b-form>
              </b-card-text>
            </b-card>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </FooterWrapper>
</template>
